import {
    LoaderFunction,
} from 'react-router-dom'
import {
    SkyNetTableField,
} from 'app/shared-components/SkyNetTable'
import {
    RequestKeys,
} from 'app/hooks/useRequest'
import {
    SidebarTab,
} from 'app/shared-components/SkyNetDomain'
import {
    ActionBulkComponentFunction,
    ActionBatchComponentAction,
    ActionComponentFunction,
} from 'app/shared-components/SkyNetDomain/SkyNetDomain.types'
import {
    CustomRouteObjectProps,
} from 'app/SkyNetRoutes/SkyNetRoutes.types'
import {
    RequestConfig,
} from 'app/types/request.types'

export type SkyNetLoaderResponse<Data extends {}> = {
    data?: Data,
    invalidate?: () => void
}

type ActionComponentWithCustomProps<ComponentFunction> = {
    component: ComponentFunction,
} & CustomRouteObjectProps

export enum ActionComponentsKey {
    Create = 'Create',
    Print = 'Print',
    BulkCreate = 'BulkCreate',
    BulkEdit = 'BulkEdit',
    BatchUpdate = 'BatchUpdate',
    CsvUpload = 'CsvUpload',
}

export type ActionComponentsType = {
    [ActionComponentsKey.Create]?: ActionComponentWithCustomProps<ActionComponentFunction>,
    [ActionComponentsKey.Print]?: ActionComponentWithCustomProps<ActionComponentFunction>,
    [ActionComponentsKey.BulkCreate]?: ActionComponentWithCustomProps<ActionBulkComponentFunction>,
    [ActionComponentsKey.BulkEdit]?: ActionComponentWithCustomProps<ActionBulkComponentFunction>,
    [ActionComponentsKey.BatchUpdate]?:
        ActionComponentWithCustomProps<ActionBatchComponentAction>,
    [ActionComponentsKey.CsvUpload]?:
        ActionComponentWithCustomProps<ActionBatchComponentAction>,
}

export type AdditionalRootComponents = Pick<
    ActionComponentsType,
    ActionComponentsKey.BatchUpdate | ActionComponentsKey.CsvUpload
>

// TODO-DK: 1) add implementation for RequestConfigFunction
// TODO-DK: 2) add implementation
//  for columns ((params?: Record<string, any>) => SkyNetTableField<TData>[])
// TODO-DK: 3) add implementation for formViews ((params?: Record<string, any>) => SidebarTab[])
export type SkyNetMasterTabConfig<TData> = {
    name: string
    url: string
    requestKey: RequestKeys
    request: RequestConfig /* | RequestConfigFunction */
    columns?:
        | SkyNetTableField<TData>[] // TODO-DK: rename types
        | ((params?: Record<string, any>) => SkyNetTableField<TData>[])
    actionComponents?: ActionComponentsType
    formViews?: FormViews,
    loader?: LoaderFunction,
} & CustomRouteObjectProps

export type FormViews = SidebarTab[] | ((params?: Record<string, any>) => SidebarTab[])

//
// Helper: For each property, either domain-level or tab-level.
//
type ObjectOrTabProp<TData, Key extends keyof SkyNetMasterTabConfig<TData>> =
// Domain-level: property exists here, and is omitted from tabs
    | {
    [K in Key]-?: NonNullable<SkyNetMasterTabConfig<TData>[K]>
} & {
    config: Omit<SkyNetMasterTabConfig<TData>, Key>[]
}
    // Tab-level: property forbidden here, but required in each tab
    | {
    [K in Key]?: never
} & {
    config: (Omit<SkyNetMasterTabConfig<TData>, Key> &
        Required<Pick<SkyNetMasterTabConfig<TData>, Key>>)[]
}

type OptionalObjectOrTabProp<TData, Key extends keyof SkyNetMasterTabConfig<TData>> =
    | ObjectOrTabProp<TData, Key> // same behavior as before
    | {
    // property omitted everywhere
    [K in Key]?: never
} & {
    config: Omit<SkyNetMasterTabConfig<TData>, Key>[]
}

//
// Final domain config params
//
export type SkyNetObjectConfigParams<TData = any> =
    & {
        defaultTab?: number,
        config: SkyNetMasterTabConfig<TData>[],
        uniqField?: string,
        persistentKey: string,
    }
    & ObjectOrTabProp<TData, 'formViews'>
    & ObjectOrTabProp<TData, 'columns'>
    & OptionalObjectOrTabProp<TData, 'actionComponents'>

export type SkyNetObjectFormOutletContext = {
    backPath?: string;
    refreshTable?: (requestKey: RequestKeys) => void;
}

export type SkyNetObjectTabsOutletContext = {
    refreshTable?: (requestKey: RequestKeys) => void;
}
